import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Box } from "@mui/system";
import PrimaryContent from "./PrimaryContent";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Edit as EditIcon, DeleteOutline as DeleteOutlineIcon, } from '@mui/icons-material';
import NCRFormLabel from "./NCRFormLabel";
import ExportButton from "./ExportButton";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import { useState } from "react";
import { getHeaders } from "../../Helpers/NCRHelpers";
import axios from "axios";
import { useCallback } from "react";

const baseColumns = [
  { id: "sno", label: "S.NO", width: 80 },
  { id: "ncr_id", label: "NC ID", width: 120 },
  { id: "date_of_issue", label: "NC Date", width: 160 },
  { id: "aging_target", label: "Aging", minWidth: 100 },
  { id: "affected_item_component_no", label: "Part/Component Number", minWidth: 280 },
  { id: "affected_item_revision", label: "Revision", minWidth: 100 },
  { id: "affected_item_description", label: "Description", minWidth: 350 },
  { id: "affected_item_qty", label: "Qty", minWidth: 100, },
  { id: "what_went_wrong", label: "What Went Wrong?", minWidth: 300 },
  { id: "how_was_issue_identified", label: "How Was the Issue Identified?", minWidth: 400 },
  { id: "affected_item_supplier_cust_name", label: "Customer / Supplier", minWidth: 260 },
  { id: "status", label: "Status of NC", minWidth: 150 },
  { id: "category", label: "Non-Conformance Category", minWidth: 150 },
  { id: "location", label: "Location of Non-Conformance", minWidth: 230 },
  { id: "department", label: "Department/Function", minWidth: 220 },
  { id: "reported_by", label: "Reported By", minWidth: 180 },
  { id: "corrective_action_required", label: "Corrective Action Required", minWidth: 150 },
  { id: "ncr_type", label: "NC Type", minWidth: 240 },
  { id: "responsible_team_or_person", label: "Responsible Person/Team", minWidth: 220 },
  { id: "affected_item_remarks", label: "Remarks", minWidth: 400 },
  { id: "evidence_attachments", label: "Evidence/Attachment", minWidth: 260 },
  // { id: "severity_or_priority_level", label: "NC Priority Level / Severity", minWidth: 160, },
  { id: "approving_authority", label: "Approving Authority", minWidth: 250 },
  { id: "username", label: "Created By", minWidth: 200 },
];

const additionalColumns = [
  { id: "relevant_measurements_observations_specification", label: "Relevant measurements, observations, or specifications", minWidth: 250 },
  { id: "escape_point", label: "Escape Point", minWidth: 200 },
  { id: "ndrc_identification", label: "Non-Detected Root Cause (NDRC) Identification", minWidth: 250 },
  { id: "corrective_actions_ndrc", label: "Corrective Actions for Non-Detected Root Cause", minWidth: 350 },
  { id: "process_root_cause_description", label: "Process Root Cause Description", minWidth: 250 },
  { id: "process_root_cause_identification", label: "Process Root Cause Identification", minWidth: 250 },
  { id: "corrective_actions_process_root_cause", label: "Corrective Actions for Process Root Cause (PRC)", minWidth: 250 },
  { id: "system_root_cause_identification", label: "System Root Cause (SRC) Identification", minWidth: 250 },
  { id: "corrective_actions_system_root_cause", label: "Corrective Actions for System Root Cause (SRC)", minWidth: 250 },
  { id: "preventive_action_plan", label: "Preventive Action Plan", minWidth: 300 },
  { id: "preventive_action_implementation_timeline", label: "Implementation Timeline", minWidth: 250 },
  { id: "action_attachments", label: "Attachments - Action Proof", minWidth: 250 },
  { id: "corrective_action_review_completed", label: "Has the Corrective Action been completed?", minWidth: 250 },
  { id: "is_corrective_action_review_effective_resolving_issue", label: "Is the Corrective Action effective in resolving the issue?", minWidth: 250 },
  { id: "corrective_action_review_feedback", label: "Corrective Action Review Detailed Feedback", minWidth: 400 },
  { id: "corrective_action_review_verification_evidence", label: "Corrective Action Review Verification Evidence", minWidth: 250 },
  { id: "preventive_action_review_implemented", label: "Has the Preventive Action been implemented?", minWidth: 250 },
  { id: "preventive_action_review_sufficient_prevent_recurrence", label: "Is the Preventive Action sufficient to prevent recurrence?", minWidth: 250 },
  { id: "preventive_action_review_feedback", label: "Preventive Action Review Detailed Feedback", minWidth: 400 },
  { id: "preventive_action_review_verification_evidence", label: "Preventive Action Review Verification Evidence", minWidth: 280 },
  { id: "is_ncr_ready_for_closure", label: "Is the NC ready for closure?", minWidth: 150 },
  { id: "comments_for_closure", label: "Comments for Closure", minWidth: 280 },
  { id: "copq", label: "COPQ", minWidth: 120 },
  { id: "fishbone_manpower_highlighted", label: "FB - Manpower", minWidth: 180 },
  { id: "fishbone_method_highlighted", label: "FB - Method", minWidth: 180 },
  { id: "fishbone_machine_highlighted", label: "FB - Machine", minWidth: 180 },
  { id: "fishbone_material_highlighted", label: "FB - Material", minWidth: 180 },
  { id: "fishbone_environment_highlighted", label: "FB - Environment", minWidth: 180 },
  { id: "fishbone_measurement_highlighted", label: "FB - Measurement", minWidth: 180 },
  { id: "why_1", label: "Why Why Analysis - 1", minWidth: 200 },
  { id: "why_2", label: "Why Why Analysis - 2", minWidth: 200 },
  { id: "why_3", label: "Why Why Analysis - 3", minWidth: 200 },
  { id: "why_4", label: "Why Why Analysis - 4", minWidth: 200 },
  { id: "why_5", label: "Why Why Analysis - 5", minWidth: 200 },
];

const userColumns = [
  { id: "sno", label: "S.NO", width: 60 },
  { id: "clientId", label: "Client ID", width: 100 },
  { id: "username", label: "User Name", minWidth: 180 },
  { id: "fullname", label: "Client Name", minWidth: 200 },
  { id: "email", label: "Email Address", minWidth: 330 },
  { id: "role_name", label: "Role", minWidth: 150 },
  { id: "actions", label: "Actions", width: 100 },
];

export default function NCRTable({ data, onEdit = null, onDelete = null, onExport = false }) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [scrollLeft, setScrollLeft] = React.useState(0);
  const [ncrLists, setNcrLists] = React.useState([]);
  const tableContainerRef = React.useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  let columns = [...baseColumns];

  if (!(data.page === "review" || data.page === "action")) {
    columns = columns.filter(col => col.id !== "aging_target");
  }

  if (data.page === "datatable") {
    columns = [...columns, ...additionalColumns];
  }

  if (data.page === "users") {
    columns = [...userColumns];
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleScroll = () => {
    if (tableContainerRef.current) {
      setScrollLeft(tableContainerRef.current.scrollLeft);
    }
  };

  React.useEffect(() => {
    if (onExport) {
      exportToExcel();
    }
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleScroll);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExport]);

  const exportToExcel = () => {
    if (data?.formData?.length === 0) {
      console.error("No data available to export");
      return;
    }
    const filteredColumns = columns.filter(col => col.id !== "actions");
    const formattedData = data?.formData.map((item, index) => {
      return filteredColumns.reduce((acc, col) => {
        let value;
        if (col.id === "sno") {
          value = index + 1 || "";
        } else if (["date_of_issue", "preventive_action_implementation_timeline"].includes(col.id)) {
          value = new Date(item[col.id]).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }) || "";
        } else if ((["evidence_attachments", "action_attachments", "corrective_action_review_verification_evidence", "preventive_action_review_verification_evidence",].includes(col.id)) && item[col.id]?.originalname) {
          value = item[col.id]?.originalname || "";
        } else if (col.id.startsWith("fishbone_")) {
          const category = col.id.split("_")[1];
          const fishboneData = item.fb_diagram?.[category];
          if (Array.isArray(fishboneData)) {
            const highlightedItem = fishboneData.find(itemData => itemData.is_highlighted);
            if (highlightedItem) {
              value = highlightedItem.data;
            } else {
              value = "-";
            }
          } else {
            value = "-";
          }
        } else if (col.id.startsWith("why_")) {
          const whyIndex = parseInt(col.id.split("_")[1]) - 1;
          const whyData = item.why_why_analysis?.[whyIndex];
          if (whyData && whyData.content) {
            value = whyData.content;
          } else {
            value = "-";
          }
        } else {
          value = item[col.id] || "";
        }
        acc[col.label] = value;
        return acc;
      }, {});
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();

    worksheet["!cols"] = columns.map(col => ({
      wch: col.width / 10 || col.minWidth / 10 || 15
    }));

    const sheetName = data.page === "users" ? "User Management Data" : "NC data";
    const fileName = data.page === "users" ? "User management data.xlsx" : "NC data.xlsx";

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const content = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(content, fileName);
  };

  const { targetData } = useSelector((state) => state.target);

  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [targetData]);

  const getNcrLists = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/ncr/stages/view`,
        getHeaders()
      );

      if (!response?.data?.ncrForm) {
        throw new Error('Invalid response structure: Missing ncrForm in API response');
      }

      const ncrFormData = Array.isArray(response.data.ncrForm)
        ? response.data.ncrForm
        : [];

      if (ncrFormData.length === 0) {
        console.warn('No NCR stages available from API response');
        setNcrLists([]);
        return;
      }

      setNcrLists(ncrFormData);
    } catch (error) {
      console.error('Error fetching NCR stages:', error.message || error);
      setNcrLists([]);
    }
  }, []);

  useEffect(() => {
    if (data.page !== "view") {
      getNcrLists();
    }
  }, [data.page, getNcrLists]);

  const repeatedItems = React.useMemo(() => {
    const sourceData = data.page !== "view"
      ? ncrLists
      : data?.formData;

    if (!sourceData?.length) {
      return {};
    }

    return sourceData.reduce((counts, form) => {
      const itemKey = form.affected_item_component_no || "Not Specified";
      counts[itemKey] = (counts[itemKey] || 0) + 1;
      return counts;
    }, {});
  }, [data.page, data?.formData, ncrLists]);


  return (
    <Box>
      {data?.page !== "users" &&
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <PrimaryContent sx={{ my: 3, fontSize: '16px', fontWeight: 700, color: '#000000' }}>
            {`Search results: `}
            <span style={{ fontSize: '#545454', color: '#545454', fontWeight: 400, paddingLeft: '10px' }}>{`(Total results:  ${data?.formData?.length})`}</span>
          </PrimaryContent>
          <ExportButton onClick={exportToExcel} />
        </Box>
      }
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer ref={tableContainerRef} sx={{ maxHeight: 440, border: "1px solid #E0E0E0", borderRadius: "5px", overflow: "auto" }}>
          <Table stickyHeader aria-label="sticky table" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    className="table-header"
                    sx={{
                      width: column.width || column.minWidth,
                      minWidth: column.minWidth,
                      left: index < 3 && data?.page !== "users" ? (index === 0 ? 0 : index === 1 ? 80 : 200) : "auto",
                      zIndex: index < 3 ? 3 : 2,
                      "&::after": index === 2 && scrollLeft > 40 && data?.page !== "users"
                        ? {
                          content: '""',
                          position: "absolute",
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: "3px",
                          background: "rgba(136, 136, 136, 0.5)",
                          boxShadow: "2px 0 5px 0px #888",
                        }
                        : {},
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody key={refreshKey}>
              {data?.formData?.length > 0 && data?.formData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((form, index) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={form.ncr_id || index}>
                  {columns.map((column, colIndex) => {
                    let value = form[column.id];

                    if (column.id.startsWith("fishbone_")) {
                      const category = column.id.split("_")[1];
                      const fishboneData = form.fb_diagram?.[category];
                      if (Array.isArray(fishboneData)) {
                        const highlightedItem = fishboneData.find(item => item.is_highlighted);
                        if (highlightedItem) {
                          value = highlightedItem.data;
                        } else {
                          value = "-";
                        }
                      } else {
                        value = "-";
                      }
                    } else if (column.id.startsWith("why_")) {
                      const whyIndex = parseInt(column.id.split("_")[1]) - 1;
                      const whyData = form.why_why_analysis?.[whyIndex];
                      if (whyData && whyData.content) {
                        value = whyData.content;
                      } else {
                        value = "-";
                      }
                    }

                    if (!value || value === "" || value === null || value === undefined) {
                      value = "-";
                    }
                    if (column.id === "ncr_id" && value && value !== "-") {
                      if (data.page === 'datatable') {
                        value = (form.ncr_id);
                      } else {
                        value = (
                          <Link
                            to={`/ncpro/${data?.newPage ? data?.newPage : data.page}/${form.ncr_id}`}
                            style={{ color: "#3853FF", textDecoration: "none" }}
                            state={{ isRefreshValue: data?.newPage ? data?.newPage : data.page }}
                          >
                            {form.ncr_id}
                          </Link>
                        );
                      }
                    }
                    if (column.id === "aging_target") {
                      const currentDate = new Date(new Date().toISOString());
                      const ncrDate = new Date(form.date_of_issue);
                      currentDate.setHours(0, 0, 0, 0);
                      ncrDate.setHours(0, 0, 0, 0);

                      const diffTime = Math.abs(currentDate - ncrDate);

                      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                      value = diffDays;

                      const status = form.status;
                      const target = status === "In Action" ? targetData.aging_target_action : targetData.aging_target_review
                      if (target > 0) {
                        const isOverdue = value > target;
                        value = (
                          <span style={{ color: isOverdue ? 'red' : 'inherit' }}>
                            {diffDays}
                          </span>
                        );
                      }

                    }
                    if (["date_of_issue", "preventive_action_implementation_timeline"].includes(column.id) && value && value !== "-") {
                      value = (
                        <time dateTime={value}>
                          {new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", })}
                        </time>
                      );
                    }
                    if (["evidence_attachments", "action_attachments", "corrective_action_review_verification_evidence", "preventive_action_review_verification_evidence"].includes(column.id)) {
                      value = typeof value === "object" && value?.originalname ? value = value.originalname : '-';
                    }

                    if (column.id === "affected_item_component_no") {
                      const item = value || 'Not Specified';
                      const count = repeatedItems[item] || 1;
                      value = <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ minWidth: "70%", whiteSpace: "normal", wordWrap: "break-word" }}>{item}</span>
                        <span style={{ minWidth: "30%", marginRight: "35px" }}>
                          {count > 5 ? `(>5x)` : count > 1 ? `(${count}x)` : ''}
                        </span>
                      </div>
                    }

                    if (column.id === "actions" && (onEdit || onDelete)) {
                      return (
                        <TableCell key={column.id} align="left" sx={{ background: '#fff' }}>
                          <EditIcon
                            sx={{ mr: 1, '&:hover': { cursor: 'pointer' } }}
                            onClick={() => onEdit && onEdit(form)}
                          />
                          <DeleteOutlineIcon
                            sx={{ "&:hover": { cursor: "pointer", color: "darkred" } }}
                            onClick={() => onDelete && onDelete(form)}
                          />
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        className="table-cell"
                        sx={{
                          width: column.width || column.minWidth,
                          minWidth: column.minWidth,
                          position: colIndex < 3 && data?.page !== "users" ? "sticky" : "static",
                          left: colIndex === 0 ? 0 : colIndex === 1 ? 80 : colIndex === 2 ? 200 : "auto",
                          zIndex: colIndex < 3 ? 2 : 1,
                          "&::after": colIndex === 2 && scrollLeft > 40 && data?.page !== "users"
                            ? {
                              content: '""',
                              position: "absolute",
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: "3px",
                              background: "rgba(136, 136, 136, 0.5)",
                              boxShadow: "2px 0 5px 0px #888",
                            }
                            : {},
                        }}
                      >
                        {colIndex === 0 ? String(page * rowsPerPage + index + 1).padStart(2, '0') : value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={data?.userData?.roleCode === "CLTADM" ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } : {}}>
          {data?.userData?.roleCode === "CLTADM" &&
            <NCRFormLabel isRequired={true} sx={{ m: 1, opacity: '90%' }}>Allowed the admin to create only three users.</NCRFormLabel>
          }
          <TablePagination
            sx="ncr-table-pagination"
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={data?.formData?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </Box>
  );
}